import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import api from '../api/api';
import { trackABTestEvent } from '../utils/abTesting';
import '../styles/pages/IngredientCheckPage.css';

/**
 * IngredientCheckPage - 商品成分表チェックページ
 * つむぎデザインシステム準拠、BEM記法
 *
 * フロー:
 * 1. 免責事項の同意
 * 2. 写真撮影 or ギャラリー選択
 * 3. AI分析中
 * 4. 結果ページへ遷移
 */
const IngredientCheckPage = () => {
  const { user, plan } = useAuth();
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // ページタイトル設定
  useEffect(() => {
    document.title = '成分チェック | つむぎ';
  }, []);

  // 免責事項同意
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pendingInputRef, setPendingInputRef] = useState(null);

  // 写真関連
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // 分析中状態
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState('');

  // 使用回数
  const [usage, setUsage] = useState({ count: 0, limit: 0 });

  /**
   * カメラ撮影ボタン
   */
  const handleCameraClick = () => {
    if (!agreedToTerms) {
      setPendingInputRef(cameraInputRef);
      setShowTermsModal(true);
      return;
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  /**
   * ギャラリー選択ボタン
   */
  const handleGalleryClick = () => {
    if (!agreedToTerms) {
      setPendingInputRef(fileInputRef);
      setShowTermsModal(true);
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /**
   * 写真選択時の処理
   */
  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ファイルサイズチェック (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください');
      return;
    }

    // 画像タイプチェック
    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      setError('JPEG、PNG、WEBP形式の画像のみアップロード可能です');
      return;
    }

    setError('');
    setPhoto(file);

    // プレビュー生成
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  /**
   * 分析開始
   */
  const handleAnalyze = async () => {
    if (!photo) {
      setError('写真を選択してください');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    // Track event
    if (user?._id) {
      trackABTestEvent('INGREDIENT_CHECK', user._id, 'analysis_start', {
        userPlan: plan
      });
    }

    try {
      const formData = new FormData();
      formData.append('image', photo);

      const response = await api.post('/api/ingredient-check', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // 結果をlocalStorageに保存して結果ページへ遷移
        localStorage.setItem('ingredientCheckResult', JSON.stringify({
          ingredients: response.data.ingredients,
          alerts: response.data.alerts,
          usage: response.data.usage
        }));

        // Track success
        if (user?._id) {
          trackABTestEvent('INGREDIENT_CHECK', user._id, 'analysis_complete', {
            success: true,
            userPlan: plan
          });
        }

        navigate('/ingredient-check-result');
      } else {
        throw new Error(response.data.message || '分析に失敗しました');
      }
    } catch (err) {
      console.error('Ingredient check error:', err);

      // Track failure
      if (user?._id) {
        trackABTestEvent('INGREDIENT_CHECK', user._id, 'analysis_complete', {
          success: false,
          error: err.response?.status === 429 ? 'quota_exceeded' : 'analysis_failed',
          userPlan: plan
        });
      }

      if (err.response?.status === 429) {
        setError('今月は上限までご利用いただきました。来月またご利用いただけます。');
      } else {
        setError(err.response?.data?.message || '分析に失敗しました。もう一度お試しください。');
      }
      setIsAnalyzing(false);
    }
  };

  /**
   * やり直し
   */
  const handleReset = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setError('');
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * 免責事項モーダル
   */
  const TermsModal = () => (
    <div className="ingredient-check-page__modal-overlay" onClick={() => setShowTermsModal(false)}>
      <div className="ingredient-check-page__modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ingredient-check-page__modal-title">成分チェック機能のご利用にあたって</h3>

        <div className="ingredient-check-page__modal-content">
          <p className="ingredient-check-page__modal-text">
            この機能は、一般的な栄養の知識に基づいて食品成分の情報を提供します。
          </p>

          <div className="ingredient-check-page__modal-warning">
            <h4 className="ingredient-check-page__modal-warning-title">重要な注意事項</h4>
            <ul className="ingredient-check-page__modal-list">
              <li>医療行為ではありません</li>
              <li>診断・治療を目的としたものではありません</li>
              <li>最終判断はご自身で行ってください</li>
            </ul>
          </div>

          <p className="ingredient-check-page__modal-text">
            詳しくは、利用規約および免責事項をご確認ください。
          </p>
        </div>

        <div className="ingredient-check-page__modal-actions">
          <label className="ingredient-check-page__modal-checkbox">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <span>理解しました</span>
          </label>

          <button
            className="tsumugi-btn tsumugi-btn-primary"
            onClick={() => {
              if (agreedToTerms) {
                setShowTermsModal(false);
                setTimeout(() => {
                  if (pendingInputRef?.current) {
                    pendingInputRef.current.click();
                  }
                  setPendingInputRef(null);
                }, 100);
              }
            }}
            disabled={!agreedToTerms}
          >
            同意して続ける
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <AppLayout>
      <PageTitle
        title="商品成分表チェック"
        subtitle="つむぎが成分を分析します"
        icon="🔍"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="tsumugi-container ingredient-check-page">
        {/* 分析中オーバーレイ */}
        {isAnalyzing && (
          <div className="ingredient-check-page__analyzing-overlay">
            <div className="ingredient-check-page__analyzing-card">
              {/* つむぎ画像 */}
              <div className="ingredient-check-page__analyzing-tsumugi">
                <img
                  src="/tsumugi.png"
                  alt="つむぎ"
                  className="ingredient-check-page__tsumugi-image"
                />
              </div>
              <div className="ingredient-check-page__analyzing-spinner"></div>
              <p className="ingredient-check-page__analyzing-title">
                成分を見ているよ...
              </p>
              <p className="ingredient-check-page__analyzing-subtitle">
                橋本病への影響を確認しています
              </p>
              <div className="ingredient-check-page__analyzing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* 免責事項モーダル */}
        {showTermsModal && <TermsModal />}

        {/* 使い方ガイド */}
        {!photoPreview && (
          <div className="tsumugi-card tsumugi-card--shell ingredient-check-page__guide">
            <h3 className="tsumugi-card-title">📸 こんな使い方ができます</h3>

            <div className="ingredient-check-page__guide-list">
              <div className="ingredient-check-page__guide-item">
                <div className="ingredient-check-page__guide-icon">🏪</div>
                <div>
                  <p className="ingredient-check-page__guide-title">スーパーでの買い物中</p>
                  <p className="ingredient-check-page__guide-description">商品裏面の成分表を撮影してチェック</p>
                </div>
              </div>

              <div className="ingredient-check-page__guide-item">
                <div className="ingredient-check-page__guide-icon">💻</div>
                <div>
                  <p className="ingredient-check-page__guide-title">ネットスーパー</p>
                  <p className="ingredient-check-page__guide-description">商品ページをスクリーンショットして確認</p>
                </div>
              </div>

              <div className="ingredient-check-page__guide-item">
                <div className="ingredient-check-page__guide-icon">🎁</div>
                <div>
                  <p className="ingredient-check-page__guide-title">新しい商品を試すとき</p>
                  <p className="ingredient-check-page__guide-description">購入前に成分をチェックして安心</p>
                </div>
              </div>
            </div>

            <div className="ingredient-check-page__guide-tips">
              <p className="ingredient-check-page__guide-tips-title">💡 撮影のコツ</p>
              <ul className="ingredient-check-page__guide-tips-list">
                <li>明るい場所で撮影</li>
                <li>成分表全体が入るように</li>
                <li>文字がはっきり見えるように</li>
              </ul>
            </div>
          </div>
        )}

        {/* エラー表示 */}
        {error && (
          <div className="tsumugi-card tsumugi-card--shell ingredient-check-page__error">
            <div className="ingredient-check-page__error-icon">❌</div>
            <p className="ingredient-check-page__error-text">{error}</p>
          </div>
        )}

        {/* 写真プレビュー */}
        {photoPreview && (
          <div className="tsumugi-card ingredient-check-page__preview">
            <h3 className="tsumugi-card-title">撮影した写真</h3>
            <div className="ingredient-check-page__preview-image-wrapper">
              <img
                src={photoPreview}
                alt="成分表プレビュー"
                className="ingredient-check-page__preview-image"
              />
            </div>
            <button
              className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--gray ingredient-check-page__preview-reset"
              onClick={handleReset}
            >
              撮り直す
            </button>
          </div>
        )}

        {/* 撮影ボタン */}
        {!photoPreview && (
          <div className="ingredient-check-page__camera-section">
            {/* カメラ用input（capture属性あり） */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              capture="environment"
              onChange={handlePhotoSelect}
              className="ingredient-check-page__camera-input"
            />

            {/* ギャラリー用input（capture属性なし） */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handlePhotoSelect}
              className="ingredient-check-page__camera-input"
            />

            <button
              className="tsumugi-btn tsumugi-btn-primary ingredient-check-page__camera-btn"
              onClick={handleCameraClick}
            >
              📷 成分表を撮影する
            </button>

            <button
              className="tsumugi-btn tsumugi-btn-outline tsumugi-btn-outline--mint ingredient-check-page__gallery-btn"
              onClick={handleGalleryClick}
            >
              🖼️ ギャラリーから選択
            </button>
          </div>
        )}

        {/* 分析ボタン */}
        {photoPreview && (
          <button
            className="tsumugi-btn tsumugi-btn-primary ingredient-check-page__analyze-btn"
            onClick={handleAnalyze}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? '分析中...' : '✨ つむぎに分析してもらう'}
          </button>
        )}

        {/* 免責事項（常に表示） */}
        <div className="ingredient-check-page__disclaimer">
          <div className="ingredient-check-page__disclaimer-content">
            <span className="ingredient-check-page__disclaimer-icon">ℹ️</span>
            <p className="ingredient-check-page__disclaimer-text">
              この機能は一般的な栄養の知識に基づく情報提供です。
              医療行為ではありません。最終判断はご自身で行ってください。
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default IngredientCheckPage;
